import type { SupportedLocale } from './enums';

export const CustomerStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
} as const;
export type CustomerStatus = (typeof CustomerStatus)[keyof typeof CustomerStatus];

export const Gender = {
  MALE: 'MALE',
  FEMALE: 'FEMALE',
  OTHER: 'OTHER',
  UNSPECIFIED: 'UNSPECIFIED',
} as const;
export type Gender = (typeof Gender)[keyof typeof Gender];

/** The language a customer prefers to be contacted in. Mirrors the UI locales. */
export const CustomerLanguage = {
  AR: 'AR',
  EN: 'EN',
} as const;
export type CustomerLanguage = (typeof CustomerLanguage)[keyof typeof CustomerLanguage];

export const CUSTOMER_LANGUAGE_LOCALE: Record<CustomerLanguage, SupportedLocale> = {
  AR: 'ar',
  EN: 'en',
};

/**
 * Fields the completeness and readiness rules can reason about.
 *
 * A closed union rather than free strings, so a rule referring to a field that no longer exists
 * is a compile error instead of a requirement nobody can ever satisfy.
 */
export const CustomerField = {
  DISPLAY_NAME: 'displayName',
  MOBILE: 'mobile',
  EMAIL: 'email',
  DATE_OF_BIRTH: 'dateOfBirth',
  GENDER: 'gender',
  PREFERRED_LANGUAGE: 'preferredLanguage',
} as const;
export type CustomerField = (typeof CustomerField)[keyof typeof CustomerField];

/**
 * Business contexts a customer record can be evaluated against.
 *
 * Only `CUSTOMER_BASIC` has rules in Phase 1. The rest are declared so the frontend and the rule
 * registry share one vocabulary as later modules fill them in — see docs/customers.md.
 */
export const CustomerContext = {
  CUSTOMER_BASIC: 'CUSTOMER_BASIC',
} as const;
export type CustomerContext = (typeof CustomerContext)[keyof typeof CustomerContext];

export interface CustomerSummary {
  id: string;
  customerNumber: string;
  displayName: string;
  firstName: string;
  middleName: string | null;
  lastName: string | null;
  mobile: string;
  alternateMobile: string | null;
  email: string | null;
  dateOfBirth: string | null;
  gender: Gender;
  preferredLanguage: CustomerLanguage;
  status: CustomerStatus;
  notes: string | null;
  createdBranchId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** The trimmed shape used by search results and duplicate warnings. */
export interface CustomerMatch {
  id: string;
  customerNumber: string;
  displayName: string;
  mobile: string;
  email: string | null;
  status: CustomerStatus;
}

export interface DuplicateCheckResult {
  hasPossibleDuplicates: boolean;
  matches: (CustomerMatch & {
    /** Why this record surfaced, strongest signal first. */
    reason: 'MOBILE' | 'EMAIL' | 'NAME';
  })[];
}

export interface ProfileCompleteness {
  percentage: number;
  missingFields: CustomerField[];
  /** Fields that are present, so the UI can show progress rather than only gaps. */
  completedFields: CustomerField[];
}

export interface CustomerReadiness {
  context: CustomerContext;
  ready: boolean;
  missingRequired: CustomerField[];
  missingOptional: CustomerField[];
}

/** One entry in the customer timeline. Phase 1 sources these from the audit trail only. */
export interface CustomerTimelineEntry {
  id: string;
  action: string;
  entityType: string;
  at: string;
  actorName: string | null;
  /**
   * Safe business detail for a communication entry: what it was about, on which channel, how it
   * ended and from which branch. Never the body, never a number, never anything about the provider.
   */
  communication?: {
    purpose: string;
    channel: string;
    status: string;
    branchId: string;
  };
}
